
import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import SidePanelRoot from './SidePanelRoot';
import './side-panel.scss';

const SidePanelContext = createContext(null);

export function SidePanelProvider({ children }) {
  const [isOpen, setOpen] = useState(false);
  const [panelContent, setPanelContent] = useState(null);

  const [options, setOptions] = useState({
    width: '50%',
    position: 'right',
    backdropClosable: true,
    ariaLabel: 'Side panel',
    panelClassName: '',
    backdropClassName: '',
  });

  const openPanel = useCallback((content, opts = {}) => {
    setPanelContent(content);
    setOptions((prev) => ({
      ...prev,
      ...opts,
    }));
    setOpen(true);
  }, []);

  const closePanel = useCallback(() => {
    setOpen(false);
    setTimeout(() => setPanelContent(null), 250);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    
    document.body.classList.add('side-panel-open');
    return () => {
      document.body.classList.remove('side-panel-open');
    };
  }, [isOpen]);

  const value = useMemo(() => ({
    isOpen,
    openPanel,
    closePanel,
    options,
  }), [isOpen, openPanel, closePanel, options]);

  return (
    <SidePanelContext.Provider value={value}>
      {children}
      <SidePanelRoot
        isOpen={isOpen}
        options={options}
        onBackdrop={() => options.backdropClosable && closePanel()}
        onClose={closePanel}
      >
        {panelContent}
      </SidePanelRoot>
    </SidePanelContext.Provider>
  );
}

export function useSidePanel() {
  const ctx = useContext(SidePanelContext);
  if (!ctx) throw new Error('useSidePanel must be used within <SidePanelProvider>');
  return ctx;
}
